import { Edge, type Node, type ImportData } from '@/types/node';
import { NodeType } from '@/enums/node-type.enum';
import { I18N_KEYS } from '@/language/keys';
import { GroupBounds } from '@/components/Recipe/Views/NodeGrouping/groups.types';
import { DUPLICATE_NODE_OFFSET_X } from '@/components/Recipe/consts/ui';
import { ZIndex } from '@/consts/z-indexes.consts';
import { TEXT_ITERATOR_ID, ITERATOR_ID_BY_FILE_TYPE } from '../packUnpack.consts';
import type { PackableFileType, PackUnpackItem } from '../packUnpack.consts';
import type { FileKind } from 'web';

export type XYPosition = { x: number; y: number };
export type NodeWithZIndex = Node & { zIndex?: number };

/**
 * Categorizes nodes into regular, group, and children categories
 */
const categorizeNodes = (nodes: Node[]) => {
  const regularNodes: Node[] = [];

  const groupNodes = nodes.filter((node) => node.type === NodeType.CustomGroup);
  const groupNodeIds = new Set<string>(groupNodes.map((group) => group.id));

  nodes.forEach((node) => {
    if (node.type !== NodeType.CustomGroup) {
      if (node.parentId && !groupNodeIds.has(node.parentId)) {
        regularNodes.push({ ...node, parentId: undefined }); // migrate nodes with non-existing parentId to regular nodes
      } else {
        regularNodes.push(node);
      }
    }
  });

  return { regularNodes, groupNodes };
};

/**
 * Stacks nodes and edges for proper z-ordering in rendering.
 * The stacking order is:
 * 1. Group nodes - default z-index (0)
 * 2. Edges - z-index (1)
 * 3. Regular nodes - z-index (2)
 *
 * @returns The nodes and edges with z-index properly set for rendering
 */
export const stackNodesAndEdgesForRendering = (
  nodes: Node[],
  edges: Edge[],
): { nodes: NodeWithZIndex[]; edges: (Edge & { zIndex?: number })[] } => {
  const { regularNodes, groupNodes } = categorizeNodes(nodes);
  const nodeResults = [...groupNodes, ...regularNodes.map((node) => ({ ...node, zIndex: ZIndex.Flow.REGULAR_NODE }))];
  const stackedEdges = edges.map((edge) => ({
    ...edge,
    zIndex: ZIndex.Flow.EDGES,
  }));

  return { nodes: nodeResults, edges: stackedEdges };
};

/**
 * Calculates the position of a duplicate node adjacent to the original node
 * @param node - The original node
 * @returns The adjacent position of the duplicate node
 */
export const getAdjacentPositionForNodeDuplication = (node: Node): XYPosition => {
  return {
    x: node.position.x + (node.width ?? 0) + DUPLICATE_NODE_OFFSET_X,
    y: node.position.y + (node.height ?? 0) / 3,
  };
};

/**
 * Converts a node's absolute position to relative position within a group
 */
export const getNodePositionInsideParent = (node: Partial<Node>, groupNode: Node): XYPosition => {
  const position = node.position ?? { x: 0, y: 0 };
  const nodeWidth = node.width ?? 0;
  const nodeHeight = node.height ?? 0;
  const groupWidth = groupNode.width ?? 0;
  const groupHeight = groupNode.height ?? 0;

  let relativeX: number;
  let relativeY: number;

  if (position.x < groupNode.position.x) {
    relativeX = 0;
  } else if (position.x + nodeWidth > groupNode.position.x + groupWidth) {
    relativeX = groupWidth - nodeWidth;
  } else {
    relativeX = position.x - groupNode.position.x;
  }

  if (position.y < groupNode.position.y) {
    relativeY = 0;
  } else if (position.y + nodeHeight > groupNode.position.y + groupHeight) {
    relativeY = groupHeight - nodeHeight;
  } else {
    relativeY = position.y - groupNode.position.y;
  }

  return { x: relativeX, y: relativeY };
};

/**
 * Converts a node's relative position within a group to absolute position
 */
export const getAbsolutePositionFromRelative = (relativePosition: XYPosition, groupNode: Node): XYPosition => {
  return {
    x: relativePosition.x + groupNode.position.x,
    y: relativePosition.y + groupNode.position.y,
  };
};

/**
 * Calculates bounds for a group of nodes with optional padding
 */
export const calculateMultiNodeBoundsWithPadding = (
  nodes: Node[],
  padding: number,
): { x: number; y: number; width: number; height: number } => {
  const bounds = getNodesBounds(nodes);
  return {
    x: bounds.x - padding,
    y: bounds.y - padding,
    width: bounds.width + 2 * padding,
    height: bounds.height + 2 * padding,
  };
};

/**
 * Calculates the expanded bounds needed to include new nodes in an existing group.
 * Handles conversion of existing children from relative to absolute positions internally.
 * @param groupNode - The existing group node
 * @param existingChildrenRelative - Existing child nodes with relative positions
 * @param newNodes - New nodes to add with absolute positions
 * @param padding - Padding around children inside the group
 */
export const calculateExpandedGroupBounds = (
  groupNode: Node,
  existingChildrenRelative: Node[],
  newNodes: Node[],
  padding: number = 0,
): { x: number; y: number; width: number; height: number } => {
  // Convert existing children to absolute positions for bounds calculation
  const existingChildrenAbsolute = existingChildrenRelative.map((child) =>
    convertChildPositionToAbsolute(child, groupNode.position),
  );

  // Calculate bounds for all nodes (existing + new)
  // Use calculateMultiNodeBoundsWithPadding to get padded bounds for children
  const selectionBoundsChildrenPadded = calculateMultiNodeBoundsWithPadding(
    [...existingChildrenAbsolute, ...newNodes],
    padding,
  );

  // Get the group's current bounds (which already include padding)
  const currentGroupBounds = {
    x: groupNode.position.x,
    y: groupNode.position.y,
    width: groupNode.width ?? 0,
    height: groupNode.height ?? 0,
  };

  // Take the minimum for top-left (group can expand left/up if needed)
  const newGroupX = Math.min(currentGroupBounds.x, selectionBoundsChildrenPadded.x);
  const newGroupY = Math.min(currentGroupBounds.y, selectionBoundsChildrenPadded.y);

  // Take the maximum for bottom-right (group never shrinks, but can expand)
  const newGroupBottom = Math.max(
    currentGroupBounds.y + currentGroupBounds.height,
    selectionBoundsChildrenPadded.y + selectionBoundsChildrenPadded.height,
  );
  const newGroupRight = Math.max(
    currentGroupBounds.x + currentGroupBounds.width,
    selectionBoundsChildrenPadded.x + selectionBoundsChildrenPadded.width,
  );

  const newGroupWidth = newGroupRight - newGroupX;
  const newGroupHeight = newGroupBottom - newGroupY;

  return {
    x: newGroupX,
    y: newGroupY,
    width: newGroupWidth,
    height: newGroupHeight,
  };
};

/**
 * Converts child node position from absolute to relative when adding to a group
 */
export const convertChildPositionToRelative = (node: Node, groupBounds: { x: number; y: number }): XYPosition => {
  return {
    x: node.position.x - groupBounds.x,
    y: node.position.y - groupBounds.y,
  };
};

export const convertChildPositionToAbsolute = (node: Node, groupBounds: { x: number; y: number }): Node => {
  return {
    ...node,
    position: {
      x: node.position.x + groupBounds.x,
      y: node.position.y + groupBounds.y,
    },
  };
};

/**
 * Converts a child's relative position from one parent coordinate system to another.
 * Useful when a group's bounds change and children need to maintain their absolute position.
 */
export const rebaseChildPosition = (
  child: Node,
  oldParentPosition: { x: number; y: number },
  newParentPosition: { x: number; y: number },
): XYPosition => {
  // Convert to absolute, then back to relative with new parent position
  const absoluteX = child.position.x + oldParentPosition.x;
  const absoluteY = child.position.y + oldParentPosition.y;
  return {
    x: absoluteX - newParentPosition.x,
    y: absoluteY - newParentPosition.y,
  };
};

/**
 * Constrains bounds to ensure children remain contained within the group.
 * Returns the union of requested bounds and minimum bounds needed to contain all children.
 */
export const constrainBoundsToChildren = (
  requestedBounds: GroupBounds,
  childNodes: Node[],
  groupPosition: XYPosition,
  padding: number,
): { x: number; y: number; width: number; height: number } => {
  // If there are no children, return the requested bounds unchanged
  if (childNodes.length === 0) {
    return requestedBounds;
  }

  const childNodesAbsolute = childNodes.map((child) => convertChildPositionToAbsolute(child, groupPosition));
  const groupNodeBounds = calculateMultiNodeBoundsWithPadding(childNodesAbsolute, padding);

  const newX = Math.min(requestedBounds.x, groupNodeBounds.x);
  const newY = Math.min(requestedBounds.y, groupNodeBounds.y);
  const newRight = Math.max(requestedBounds.x + requestedBounds.width, groupNodeBounds.x + groupNodeBounds.width);
  const newBottom = Math.max(requestedBounds.y + requestedBounds.height, groupNodeBounds.y + groupNodeBounds.height);

  return {
    x: newX,
    y: newY,
    width: newRight - newX,
    height: newBottom - newY,
  };
};

export const getNodesBounds = (nodes: Node[]): { x: number; y: number; width: number; height: number } => {
  if (nodes.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const x = Math.min(...nodes.map((node) => node.position.x));
  const y = Math.min(...nodes.map((node) => node.position.y));
  const bottom = Math.max(...nodes.map((node) => node.position.y + (node.height ?? 0)));
  const right = Math.max(...nodes.map((node) => node.position.x + (node.width ?? 0)));
  return {
    x,
    y,
    width: right - x,
    height: bottom - y,
  };
};

export const isModelNode = (node: Node): boolean => {
  return (
    node.type === NodeType.CustomModelV2 ||
    node.type === NodeType.WildcardV2 ||
    ('model' in node.data && node.data.model !== undefined && node.data.model !== null)
  );
};

/**
 * Finds nodes that intersect with the given node
 * @param node - The node to check for intersections
 * @param candidateNodes - The nodes to check for intersections
 * @returns The nodes that intersect with the given node
 */
export const getIntersectingNodes = (node: Node, candidateNodes: Node[]): Node[] => {
  return candidateNodes.filter((candidate) => {
    const candidateLeft = candidate.position.x;
    const candidateTop = candidate.position.y;
    const candidateRight = candidateLeft + (candidate.width ?? 0);
    const candidateBottom = candidateTop + (candidate.height ?? 0);

    const nodeLeft = node.position.x;
    const nodeTop = node.position.y;
    const nodeRight = nodeLeft + (node.width ?? 0);
    const nodeBottom = nodeTop + (node.height ?? 0);

    return (
      candidateRight > nodeLeft && candidateLeft < nodeRight && candidateBottom > nodeTop && candidateTop < nodeBottom
    );
  });
};

export const categorizeNodesGroupStatus = (
  nodes: Node[],
): {
  groupNodes: Node[];
  groupedNodes: Node[];
  ungroupedNodes: Node[];
} => {
  const groupNodes: Node[] = [];
  const groupedNodes: Node[] = [];
  const ungroupedNodes: Node[] = [];

  for (const node of nodes) {
    if (node.type === NodeType.CustomGroup) {
      groupNodes.push(node);
    } else if (node.parentId) {
      groupedNodes.push(node);
    } else {
      ungroupedNodes.push(node);
    }
  }

  return { groupNodes, groupedNodes, ungroupedNodes };
};

const ITERATOR_LABEL_KEY_BY_FILE_TYPE: Record<string, string> = {
  image: I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.CREATE_IMAGE_ITERATOR,
  video: I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.CREATE_VIDEO_ITERATOR,
};

export interface IteratorInfo {
  iteratorId: string;
  labelKey: string;
  data: Record<string, unknown>;
}

export function getIteratorInfo(items: PackUnpackItem[]): IteratorInfo | null {
  if (items.length < 2) return null;

  const allText = items.every((item) => item.type === 'text');
  if (allText) {
    const texts = items.map((item) => (item as { type: 'text'; text: string }).text);
    return {
      iteratorId: TEXT_ITERATOR_ID,
      labelKey: I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.CREATE_TEXT_ITERATOR,
      data: { list: texts, delimiter: '*', isIterator: true },
    };
  }

  const allFile = items.every((item) => item.type === 'file');
  if (allFile) {
    const files = items.map((item) => (item as { type: 'file'; file: FileKind }).file);
    const fileType = files[0].type;
    if ((fileType === 'image' || fileType === 'video') && files.every((f) => f.type === fileType)) {
      return {
        iteratorId: ITERATOR_ID_BY_FILE_TYPE[fileType],
        labelKey: ITERATOR_LABEL_KEY_BY_FILE_TYPE[fileType],
        data: {
          files: { type: 'value', data: { type: 'file_array', value: files } },
          selectedIndex: 0,
        },
      };
    }
  }

  return null;
}

export function getImportNodeFileType(node: Node): PackableFileType | null {
  const data = node.data as ImportData;
  if (!data.files || data.files.length === 0) return null;
  const type = data.files[0].type;
  if (type === 'image' || type === 'video') return type;
  return null;
}

/**
 * Stable template identity for the node: `originalName` from the server (or `data.name` when missing).
 */
export function getNodeOriginalName(node: Node): string {
  return node.originalName || node.data.name || '';
}
